import React from 'react'
import Image from 'next/image'
import NextLink from 'next/link'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import type { ButtonProps } from '@wartek-id/button-v1'

import PelaksanaanPraktikKinerjaStatusBadge from 'app/KinerjaTimPenilaiPenilikPage/components/PelaksanaanPraktikKinerjaStatusBadge'
import { usePelaksanaanPraktikKinerja } from 'app/KinerjaTimPenilaiPenilikPage/hooks/usePelaksanaanPraktikKinerja'

import type {
  StatusPelaksanaanPraktikKinerja,
  StatusPelaksanaanPraktikKinerjaMetadata,
} from 'types/kinerja-penilik-pegawai'

interface PelaksanaanPraktikKinerjaCardLinkProps
  extends React.HTMLAttributes<HTMLDivElement> {
  href: string
}
const PelaksanaanPraktikKinerjaCardLink = ({
  href,
  children,
}: PelaksanaanPraktikKinerjaCardLinkProps) => {
  if (Boolean(href)) {
    return (
      <NextLink href={href} passHref>
        {children}
      </NextLink>
    )
  }

  return <>{children}</>
}

interface PelaksanaanPraktikKinerjaCardProps
  extends React.HTMLAttributes<HTMLDivElement> {
  imageSrc: string
  title: string
  currentState: StatusPelaksanaanPraktikKinerja
  currentStateMeta?: StatusPelaksanaanPraktikKinerjaMetadata
  states: StatusPelaksanaanPraktikKinerja[]
  href?: string
  disabled?: boolean
  done?: boolean
  onCtaClick?: () => void
}

export default function PelaksanaanPraktikKinerjaCard({
  imageSrc,
  title,
  states,
  currentState,
  currentStateMeta,
  href,
  disabled = false,
  done = false,
  className,
  ...props
}: PelaksanaanPraktikKinerjaCardProps) {
  const state = React.useMemo(() => {
    if (currentState === 'SELESAI') {
      return currentState
    }
    return states.includes(currentState) ? currentState : states[0]
  }, [currentState, states])

  const {
    isDiskusiPersiapanFormSubmitted,
    isPendampinganSekolahFormSubmitted,
    isLoading,
  } = usePelaksanaanPraktikKinerja({ state: currentState })

  const buttonProps = React.useMemo((): {
    text: string
    color: ButtonProps['color']
  } | null => {
    switch (state) {
      case 'DISKUSI_PERSIAPAN':
        return currentState !== 'DISKUSI_PERSIAPAN' ||
          isDiskusiPersiapanFormSubmitted
          ? { text: 'Cek', color: 'white' }
          : null
      case 'PENDAMPINGAN_SEKOLAH':
        return !['DISKUSI_PERSIAPAN', 'PENDAMPINGAN_SEKOLAH'].includes(
          currentState
        ) || isPendampinganSekolahFormSubmitted
          ? { text: 'Cek', color: 'white' }
          : null
      case 'DISKUSI_TINDAK_LANJUT':
        return currentStateMeta?.[currentState]?.isPenilaian
          ? { text: 'Lakukan', color: 'blue' }
          : { text: 'Cek', color: 'white' }
      case 'REFLEKSI_TINDAK_LANJUT':
        return null
      case 'REFLEKSI_TINDAK_LANJUT_PENILAIAN':
        return { text: 'Lakukan', color: 'blue' }
      case 'DISKUSI_TINDAK_LANJUT_PENILAIAN':
      case 'UPAYA_TINDAK_LANJUT':
      case 'SELESAI':
      default:
        return { text: 'Cek', color: 'white' }
    }
  }, [
    currentState,
    currentStateMeta,
    isDiskusiPersiapanFormSubmitted,
    isPendampinganSekolahFormSubmitted,
    state,
  ])

  return (
    <div
      className={clsx(
        'px-6 py-4 bg-white border rounded-md border-subdued flex gap-6 border-b-subdued',
        disabled && 'grayscale !bg-gray-20',
        className
      )}
      {...props}
    >
      <Image src={imageSrc} width={64} height={64} alt="" />
      <div className="flex items-center justify-between flex-1 gap-1">
        <div className="flex flex-col gap-3">
          <Text variant="heading-sm">{title}</Text>
          <PelaksanaanPraktikKinerjaStatusBadge
            currentState={currentState}
            state={state}
            meta={currentStateMeta}
            done={done}
            disabled={disabled}
          />
        </div>
        {!isLoading && (
          <div className="ml-auto">
            {disabled && (
              <Icon color="disabled" fontSize="lg">
                lock_outline
              </Icon>
            )}

            {!disabled && Boolean(buttonProps) && (
              <PelaksanaanPraktikKinerjaCardLink href={href}>
                <Button
                  data-testid="pelaksanaan-praktik-kinerja-card-cta"
                  size="lg"
                  color={buttonProps.color}
                >
                  {buttonProps.text}
                </Button>
              </PelaksanaanPraktikKinerjaCardLink>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
